import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['input', 'requirements', 'strength', 'feedback']
  static values = {
    validateUrl: String,
    debounce: { type: Number, default: 300 },
    userInputs: { type: Array, default: [] } // User-specific data to exclude from passwords
  }

  connect () {
    this.timeout = null
    // Initial validation if there's already a value
    if (this.inputTarget.value) {
      this.validatePassword()
    }
  }

  disconnect () {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  onInput () {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    // Only show loading state immediately, don't calculate client-side
    const password = this.inputTarget.value
    if (!password) {
      this.clearAll()
      return
    }

    // Show loading indicator
    if (this.hasStrengthTarget) {
      this.strengthTarget.innerHTML = '<div class="text-sm text-gray-500">Analyse en cours...</div>'
    }

    // Debounce server validation - this is the only source of truth
    this.timeout = setTimeout(() => {
      this.validatePassword()
    }, this.debounceValue)
  }

  getUserInputs () {
    // Collect user-specific data from the form
    const inputs = [...this.userInputsValue]

    // Try to get email, firstname, lastname from the form
    const emailField = document.querySelector('input[type="email"]')
    const firstnameField = document.querySelector('input[name*="firstname"]')
    const lastnameField = document.querySelector('input[name*="lastname"]')

    if (emailField?.value) {
      inputs.push(emailField.value)
      // Also add email parts
      const emailParts = emailField.value.split('@')[0].split(/[._-]/)
      inputs.push(...emailParts)
    }

    if (firstnameField?.value) inputs.push(firstnameField.value)
    if (lastnameField?.value) inputs.push(lastnameField.value)

    // Add common words to avoid
    inputs.push('preparun', 'password', 'motdepasse')

    return inputs.filter(Boolean)
  }

  async validatePassword () {
    const password = this.inputTarget.value

    if (!password) {
      this.clearAll()
      return
    }

    try {
      const response = await fetch(this.validateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          password,
          user_inputs: this.getUserInputs()
        })
      })

      const data = await response.json()
      console.log('Password validation response:', data) // Debug log

      this.updateRequirements(data)
      this.updateStrengthFromServer(data)
      this.updateFeedbackFromServer(data.feedback)

      // Update border colors based on validation
      if (data.valid && password.length > 0) {
        this.inputTarget.classList.remove('border-red-300', 'focus:border-red-500')
        this.inputTarget.classList.add('border-green-300', 'focus:border-green-500')
      } else if (!data.valid && password.length > 0) {
        this.inputTarget.classList.remove('border-green-300', 'focus:border-green-500')
        this.inputTarget.classList.add('border-red-300', 'focus:border-red-500')
      }
    } catch (error) {
      console.error('Password validation error:', error)
    }
  }

  updateRequirements (data) {
    if (!this.hasRequirementsTarget) return

    const { requirements } = data

    // Clear existing requirements
    this.requirementsTarget.innerHTML = ''

    if (requirements && requirements.length > 0) {
      const list = document.createElement('ul')
      list.className = 'space-y-1 text-sm'

      requirements.forEach(requirement => {
        const item = document.createElement('li')
        const met = requirement.met

        item.className = met
          ? 'flex items-center text-green-600'
          : 'flex items-center text-gray-500'

        item.innerHTML = `
          <span class="mr-2">${met ? '✓' : '○'}</span>
          <span>${requirement.label}</span>
        `

        list.appendChild(item)
      })

      this.requirementsTarget.appendChild(list)
    }
  }

  updateStrengthFromServer (data) {
    if (!this.hasStrengthTarget) return

    const strengthTexts = {
      0: { text: 'Très faible', color: 'bg-red-500', textColor: 'text-red-700' },
      1: { text: 'Faible', color: 'bg-orange-500', textColor: 'text-orange-700' },
      2: { text: 'Acceptable', color: 'bg-yellow-500', textColor: 'text-yellow-700' },
      3: { text: 'Fort', color: 'bg-green-500', textColor: 'text-green-700' },
      4: { text: 'Très fort', color: 'bg-green-600', textColor: 'text-green-800' }
    }

    const strengthInfo = strengthTexts[data.strength]
    const crackTime = this.formatCrackTime(data.crack_time)

    this.strengthTarget.innerHTML = `
      <div class="space-y-2">
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-600">Force:</span>
          <div class="flex-1">
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div class="${strengthInfo.color} h-2 rounded-full transition-all duration-300" style="width: ${(data.strength + 1) * 20}%"></div>
            </div>
          </div>
          <span class="text-sm font-medium ${strengthInfo.textColor}">${strengthInfo.text}</span>
        </div>
        ${crackTime
? `
          <p class="text-xs text-gray-600">
            Temps de craquage estimé: <span class="font-medium">${crackTime}</span>
          </p>
        `
: ''}
      </div>
    `
  }

  updateFeedbackFromServer (feedback) {
    if (!this.hasFeedbackTarget) return

    if (!feedback || (!feedback.suggestions?.length && !feedback.warning)) {
      this.feedbackTarget.innerHTML = ''
      return
    }

    let feedbackHtml = '<div class="mt-2 space-y-1">'

    if (feedback.warning) {
      feedbackHtml += `
        <p class="text-xs text-orange-600 flex items-start">
          <svg class="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
          <span>${feedback.warning}</span>
        </p>
      `
    }

    if (feedback.suggestions?.length > 0) {
      feedbackHtml += '<ul class="text-xs text-gray-600 space-y-1">'
      feedback.suggestions.forEach(suggestion => {
        feedbackHtml += `
          <li class="flex items-start">
            <span class="mr-1">•</span>
            <span>${suggestion}</span>
          </li>
        `
      })
      feedbackHtml += '</ul>'
    }

    feedbackHtml += '</div>'
    this.feedbackTarget.innerHTML = feedbackHtml
  }

  formatCrackTime (timeString) {
    const translations = {
      'less than a second': 'moins d\'une seconde',
      seconds: 'secondes',
      minutes: 'minutes',
      hours: 'heures',
      days: 'jours',
      months: 'mois',
      years: 'ans',
      centuries: 'siècles'
    }

    let translated = timeString
    Object.keys(translations).forEach(key => {
      translated = translated.replace(key, translations[key])
    })

    return translated
  }

  clearAll () {
    this.clearRequirements()
    this.clearStrength()
    this.clearFeedback()
    this.inputTarget.classList.remove('border-green-300', 'focus:border-green-500', 'border-red-300', 'focus:border-red-500')
  }

  clearRequirements () {
    if (this.hasRequirementsTarget) {
      this.requirementsTarget.innerHTML = ''
    }
  }

  clearStrength () {
    if (this.hasStrengthTarget) {
      this.strengthTarget.innerHTML = ''
    }
  }

  clearFeedback () {
    if (this.hasFeedbackTarget) {
      this.feedbackTarget.innerHTML = ''
    }
  }
}
